import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
	CalendarDaysIcon,
	ClockIcon,
	ExclamationTriangleIcon,
	SunIcon,
} from '@heroicons/react/24/outline';
import type { PeopleFilters, Status } from '../../../utils/panorama/panoramaPeopleRows';
import type { TodaySnapshot } from '../../../utils/panorama/panoramaToday';
import type { PendingSummary } from '../../../utils/panorama/panoramaRowWindow';
import { KpiTile } from './KpiTile';

export interface OverviewRange {
	from: string;
	to: string;
}

interface OverviewKpiRowProps {
	activeScreen: 'dashboard' | 'table';
	tableFilters: PeopleFilters;
	today: TodaySnapshot;
	todayRange: OverviewRange;
	nextMonthDate: Date;
	nextMonthCount: number;
	nextMonthRange: OverviewRange;
	statusCounts: Record<Status | 'all', number>;
	pendingSummary: PendingSummary;
	expiredCount: number;
	expiringCount: number;
	riskCount: number;
	onShowTable: (statuses: Status[], range?: OverviewRange) => void;
	onClearStatuses: () => void;
}

function hasExactStatuses(filters: PeopleFilters, expected: Status[]): boolean {
	const current = filters.statuses ?? [];
	return current.length === expected.length && expected.every(status => current.includes(status));
}

function matchesPeriod(filters: PeopleFilters, range: OverviewRange): boolean {
	return filters.periodFrom === range.from && filters.periodTo === range.to;
}

export function OverviewKpiRow({
	activeScreen,
	tableFilters,
	today,
	todayRange,
	nextMonthDate,
	nextMonthCount,
	nextMonthRange,
	statusCounts,
	pendingSummary,
	expiredCount,
	expiringCount,
	riskCount,
	onShowTable,
	onClearStatuses,
}: OverviewKpiRowProps) {
	const isTable = activeScreen === 'table';
	const isTodayActive = isTable && hasExactStatuses(tableFilters, ['on-vacation']) && matchesPeriod(tableFilters, todayRange);
	const isNextMonthActive = isTable && hasExactStatuses(tableFilters, ['scheduled', 'on-vacation']) && matchesPeriod(tableFilters, nextMonthRange);
	const isPendingActive = isTable && hasExactStatuses(tableFilters, ['pending', 'approval-urgent']);
	const isRiskActive = isTable && hasExactStatuses(tableFilters, ['expired', 'expiring-soon']);

	return (
		<div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4">
			<KpiTile
				variant="highlight"
				icon={CalendarDaysIcon}
				tourId="overview-kpi-today"
				label="Em férias hoje"
				value={today.baseOutCount}
				subtext={`${today.outPercentage}% da base · ${today.returningCount} voltam em 7 dias`}
				onClick={() => onShowTable(['on-vacation'], todayRange)}
				active={isTodayActive}
			/>

			<KpiTile
				icon={SunIcon}
				tourId="overview-kpi-next-month"
				label="Férias no próximo mês"
				value={nextMonthCount}
				subtext={`${format(nextMonthDate, 'MMMM', { locale: ptBR })} · agendados e em férias`}
				onClick={() => (isNextMonthActive ? onClearStatuses() : onShowTable(['scheduled', 'on-vacation'], nextMonthRange))}
				active={isNextMonthActive}
			/>

			<KpiTile
				icon={ClockIcon}
				tourId="overview-kpi-pending"
				label="Aguardando aprovação"
				value={(statusCounts['pending'] ?? 0) + (statusCounts['approval-urgent'] ?? 0)}
				subtext={
					pendingSummary.oldestDays !== null
						? `${statusCounts['approval-urgent'] ?? 0} urgentes · mais antigo há ${pendingSummary.oldestDays}d`
						: 'nenhum pedido pendente'
				}
				onClick={() => (isPendingActive ? onClearStatuses() : onShowTable(['pending', 'approval-urgent']))}
				active={isPendingActive}
			/>

			<KpiTile
				variant="critical"
				icon={ExclamationTriangleIcon}
				tourId="overview-kpi-risk"
				label="Risco de pagar em dobro"
				value={riskCount}
				subtext={`${expiredCount} vencidas · ${expiringCount} vencendo em até 90 dias`}
				onClick={() => (isRiskActive ? onClearStatuses() : onShowTable(['expired', 'expiring-soon']))}
				active={isRiskActive}
			/>
		</div>
	);
}
